/**
 * Aceternity UI — Floating Dock
 * https://ui.aceternity.com/components/floating-dock
 * Adapted: TanStack Router `Link`, optional `onClick` items (e.g. cart), Lucide mobile toggle.
 */
import { cn } from '../../lib/utils';
import {
  AnimatePresence,
  MotionValue,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Language } from '../../contexts/languageContextDef';

/** Fixed popover above a dock tile — avoids overflow clipping from the scrollable dock row. */
function useDockPopoverPosition(
  open: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
) {
  const [pos, setPos] = useState<{ bottom: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({
        bottom: Math.max(8, window.innerHeight - r.top + 10),
        left: r.left + r.width / 2,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  return pos;
}

function useDismissOnOutside(
  open: boolean,
  onClose: () => void,
  anchorRef: React.RefObject<HTMLElement | null>,
  menuRef: React.RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onClose();
    };
    // Defer so the opening tap does not immediately dismiss.
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', close);
      document.addEventListener('touchstart', close, { passive: true });
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open, onClose, anchorRef, menuRef]);
}

type DockKeyFields = {
  /** Stable id for spotlight / “I am here” anchoring (e.g. `calendar`). */
  dockKey?: string;
};

export type FloatingDockItem =
  | (DockKeyFields & { title: string; description?: string; icon: React.ReactNode; to: string })
  | (DockKeyFields & { title: string; description?: string; icon: React.ReactNode; onClick: () => void })
  | (DockKeyFields & {
      title: string;
      description?: string;
      icon: React.ReactNode;
      languagePicker: true;
      currentLanguage: Language;
      onSelectLanguage: (lang: Language) => void;
      languageOptions: Array<{ code: Language; label: string }>;
    })
  | (DockKeyFields & {
      /** Mobile shell (&lt;744px) only — richer account + portal/logout; pad/desktop use plain account tile. */
      mobileSignedInAccount: true;
      title: string;
      description?: string;
      initials: string;
      portalLabel: string;
      logoutLabel: string;
      onOpenPortal: () => void;
      onLogout: () => void | Promise<void>;
      icon: React.ReactNode;
    });

export const FloatingDock = ({
  items,
  desktopClassName,
  mobileClassName,
  itemClassName,
  forceLight = false,
  spotlightKey = null,
  spotlightContent = null,
}: {
  items: FloatingDockItem[];
  desktopClassName?: string;
  mobileClassName?: string;
  /** Merged into each dock icon surface (desktop + mobile stack) */
  itemClassName?: string;
  forceLight?: boolean;
  /** When set, that dock tile jumps and hosts the spotlight bubble. */
  spotlightKey?: string | null;
  spotlightContent?: React.ReactNode;
}) => {
  return (
    <>
      <FloatingDockDesktop
        items={items}
        className={desktopClassName}
        itemClassName={itemClassName}
        forceLight={forceLight}
        spotlightKey={spotlightKey}
        spotlightContent={spotlightContent}
      />
      <FloatingDockMobile
        items={items}
        className={mobileClassName}
        itemClassName={itemClassName}
        forceLight={forceLight}
        spotlightKey={spotlightKey}
        spotlightContent={spotlightContent}
      />
    </>
  );
};

function DockSpotlightBubble({
  children,
  anchorRef,
}: {
  children: React.ReactNode;
  /** When set, the bubble is portaled above this node so it is never nested in a dock <button>. */
  anchorRef?: React.RefObject<HTMLElement | null>;
}) {
  const fallbackRef = useRef<HTMLElement | null>(null);
  const pos = useDockPopoverPosition(Boolean(anchorRef), anchorRef ?? fallbackRef);
  const portaled = Boolean(anchorRef);

  if (portaled && (!pos || typeof document === 'undefined')) return null;

  const bubble = (
    <motion.div
      role="status"
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.98 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className={
        portaled
          ? 'pointer-events-auto fixed z-[170] w-max max-w-[min(92vw,18rem)] -translate-x-1/2'
          : 'pointer-events-auto absolute bottom-[calc(100%+0.85rem)] left-1/2 z-[170] w-max max-w-[min(92vw,18rem)] -translate-x-1/2'
      }
      style={portaled && pos ? { bottom: pos.bottom, left: pos.left } : undefined}
    >
      <div className="relative rounded-2xl border border-[#2f9e44]/35 bg-white px-3 py-2.5 text-left shadow-lg">
        {/* Caret aimed at the calendar tile center */}
        <span
          className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-r border-b border-[#2f9e44]/35 bg-white"
          aria-hidden
        />
        {children}
      </div>
    </motion.div>
  );

  return portaled ? createPortal(bubble, document.body) : bubble;
}

function FloatingDockMobileLanguagePicker({
  item,
  itemClassName,
  forceLight,
}: {
  item: Extract<FloatingDockItem, { languagePicker: true }>;
  itemClassName?: string;
  forceLight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pos = useDockPopoverPosition(open, wrapRef);
  useDismissOnOutside(open, () => setOpen(false), wrapRef, menuRef);

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={item.title}
        title={item.title}
        className={cn(
          'flex h-11 min-h-11 w-11 min-w-11 items-center justify-center rounded-full bg-gray-200 text-[#3d2817] shadow-sm [&_svg]:text-[#3d2817]',
          forceLight ? '' : 'dark:bg-neutral-800 dark:text-neutral-100',
          itemClassName,
        )}
      >
        <div className="flex h-5 w-5 items-center justify-center font-bold [&_svg]:h-5 [&_svg]:w-5">
          {item.icon}
        </div>
      </button>
      {open && pos && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label={item.title}
              className="fixed z-[10070] flex min-w-[10rem] -translate-x-1/2 flex-col gap-0.5 rounded-xl border border-[#ece3de] bg-white/98 p-1.5 shadow-xl backdrop-blur-md"
              style={{ bottom: pos.bottom, left: pos.left }}
            >
              {item.languageOptions.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  role="option"
                  aria-selected={item.currentLanguage === opt.code}
                  onClick={() => {
                    item.onSelectLanguage(opt.code);
                    setOpen(false);
                  }}
                  className={cn(
                    'rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors',
                    item.currentLanguage === opt.code
                      ? 'bg-[#3d2817] text-white'
                      : 'text-[#3d2817] hover:bg-[#ece3de]',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function FloatingDockMobileSignedInAccount({
  item,
  itemClassName,
  forceLight,
}: {
  item: Extract<FloatingDockItem, { mobileSignedInAccount: true }>;
  itemClassName?: string;
  forceLight?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pos = useDockPopoverPosition(open, wrapRef);
  useDismissOnOutside(open, () => setOpen(false), wrapRef, menuRef);

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={item.title}
        title={item.title}
        className={cn(
          'flex h-11 min-h-11 w-11 min-w-11 items-center justify-center rounded-full border border-[#c9a77b]/45 bg-gray-200 text-[11px] font-semibold leading-none tracking-tight text-[#3d2817] shadow-sm',
          forceLight ? '' : 'dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100',
          itemClassName,
        )}
      >
        {item.initials}
      </button>
      {open && pos && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label={item.title}
              className="fixed z-[10070] flex min-w-[11rem] -translate-x-1/2 flex-col gap-0.5 rounded-xl border border-[#ece3de] bg-white/98 p-1.5 shadow-xl backdrop-blur-md"
              style={{ bottom: pos.bottom, left: pos.left }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  item.onOpenPortal();
                  setOpen(false);
                }}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#3d2817] transition-colors hover:bg-[#ece3de]"
              >
                {item.portalLabel}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  void Promise.resolve(item.onLogout()).then(() => setOpen(false));
                }}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                {item.logoutLabel}
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

const FloatingDockMobile = ({
  items,
  className,
  itemClassName,
  forceLight,
  spotlightKey,
  spotlightContent,
}: {
  items: FloatingDockItem[];
  className?: string;
  itemClassName?: string;
  forceLight?: boolean;
  spotlightKey?: string | null;
  spotlightContent?: React.ReactNode;
}) => {
  return (
    <div className={cn('mx-auto block w-max max-w-[min(92vw,520px)] min-[744px]:hidden', className)}>
      <div
        className={cn(
          // Centered chip row; popovers (language/account) can overflow above the strip.
          // Match desktop dock: light gray bar + soft outline.
          'no-scrollbar flex items-center justify-center gap-2.5 overflow-x-auto overflow-y-visible rounded-full border border-[#ece3de] bg-gray-50/95 px-3 py-2.5 shadow-lg backdrop-blur-md',
          forceLight ? '' : 'dark:border-neutral-800 dark:bg-neutral-900/95'
        )}
      >
        {items.map((item, index) => {
          const isSpotlight = Boolean(spotlightKey && item.dockKey === spotlightKey);
          if ('to' in item) {
            return (
              <Link
                key={item.title}
                to={item.to}
                className={cn(
                  'flex h-11 min-h-11 w-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[#3d2817] shadow-sm [&_svg]:text-[#3d2817]',
                  forceLight ? '' : 'dark:bg-neutral-800 dark:text-neutral-100',
                  itemClassName,
                )}
                aria-label={item.title}
                title={item.title}
              >
                <div className="flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5">
                  {item.icon}
                </div>
              </Link>
            );
          }
          if ('languagePicker' in item && item.languagePicker) {
            return (
              <FloatingDockMobileLanguagePicker
                key={`dock-lang-picker-${index}`}
                item={item}
                itemClassName={itemClassName}
                forceLight={forceLight}
              />
            );
          }
          if ('mobileSignedInAccount' in item && item.mobileSignedInAccount) {
            return (
              <FloatingDockMobileSignedInAccount
                key="dock-account-signed-in-mobile"
                item={item}
                itemClassName={itemClassName}
                forceLight={forceLight}
              />
            );
          }
          return (
            <div key={item.title} className="relative shrink-0">
              <AnimatePresence>
                {isSpotlight && spotlightContent ? (
                  <DockSpotlightBubble>{spotlightContent}</DockSpotlightBubble>
                ) : null}
              </AnimatePresence>
              <motion.button
                type="button"
                onClick={'onClick' in item ? item.onClick : undefined}
                className={cn(
                  'flex h-11 min-h-11 w-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[#3d2817] shadow-sm [&_svg]:text-[#3d2817]',
                  forceLight ? '' : 'dark:bg-neutral-800 dark:text-neutral-100',
                  isSpotlight && 'ring-2 ring-[#2f9e44]/55',
                  itemClassName,
                )}
                aria-label={item.title}
                title={item.title}
                animate={isSpotlight ? { y: [0, -7, 0] } : { y: 0 }}
                transition={
                  isSpotlight
                    ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
                    : { duration: 0.2 }
                }
              >
                <div className="flex h-5 w-5 items-center justify-center font-bold [&_svg]:h-5 [&_svg]:w-5">
                  {item.icon}
                </div>
              </motion.button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FloatingDockDesktop = ({
  items,
  className,
  itemClassName,
  forceLight,
  spotlightKey,
  spotlightContent,
}: {
  items: FloatingDockItem[];
  className?: string;
  itemClassName?: string;
  forceLight?: boolean;
  spotlightKey?: string | null;
  spotlightContent?: React.ReactNode;
}) => {
  const mouseX = useMotionValue(Infinity);
  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={cn(
        'mx-auto hidden h-16 items-end gap-4 rounded-2xl bg-gray-50 px-4 pb-3 max-[743px]:hidden min-[744px]:flex',
        forceLight ? '' : 'dark:bg-neutral-900',
        className,
      )}
    >
      {items.map((item) => (
        <IconContainer
          mouseX={mouseX}
          key={item.title}
          item={item}
          itemClassName={itemClassName}
          forceLight={forceLight}
          isSpotlight={Boolean(spotlightKey && item.dockKey === spotlightKey)}
          spotlightContent={spotlightContent}
        />
      ))}
    </motion.div>
  );
};

function DesktopLanguageDockButton({
  item,
  surface,
}: {
  item: Extract<FloatingDockItem, { languagePicker: true }>;
  surface: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-inherit"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={item.title}
      >
        {surface}
      </button>
      {open ? (
        <div
          role="listbox"
          className="absolute bottom-[calc(100%+10px)] left-1/2 z-[160] flex -translate-x-1/2 gap-1 rounded-xl border border-[#ece3de] bg-white/98 p-1.5 shadow-xl backdrop-blur-md"
        >
          {item.languageOptions.map((opt) => (
            <button
              key={opt.code}
              type="button"
              role="option"
              aria-selected={item.currentLanguage === opt.code}
              onClick={() => {
                item.onSelectLanguage(opt.code);
                setOpen(false);
              }}
              className={cn(
                'min-w-[2.5rem] rounded-lg px-2.5 py-1.5 text-center text-xs font-bold transition-colors',
                item.currentLanguage === opt.code
                  ? 'bg-[#3d2817] text-white'
                  : 'text-[#3d2817] hover:bg-[#ece3de]',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function IconContainer({
  mouseX,
  item,
  itemClassName,
  forceLight,
  isSpotlight = false,
  spotlightContent = null,
}: {
  mouseX: MotionValue<number>;
  item: FloatingDockItem;
  itemClassName?: string;
  forceLight?: boolean;
  isSpotlight?: boolean;
  spotlightContent?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const distance = useTransform(mouseX, (val) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  const widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);
  const heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40]);

  const widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);
  const heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20]);

  const width = useSpring(widthTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  const height = useSpring(heightTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  const widthIcon = useSpring(widthTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });
  const heightIcon = useSpring(heightTransformIcon, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  });

  const [hovered, setHovered] = useState(false);

  // One hover zone for icon + label: avoids flicker when the cursor crosses the gap above the icon.
  const surface = (
    <div
      className="relative inline-flex flex-col items-center gap-0"
      style={{ paddingTop: '4.75rem', marginTop: '-4.75rem' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <AnimatePresence>
        {hovered && !isSpotlight && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96, x: '-50%' }}
            animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
            exit={{ opacity: 0, y: 4, scale: 0.98, x: '-50%' }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1 min-w-[170px] max-w-[min(92vw,240px)] rounded-xl border border-white/20 bg-neutral-900/95 px-3 py-2 text-left text-neutral-50 shadow-xl backdrop-blur-md dark:border-white/10"
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-xs font-semibold leading-tight">{item.title}</span>
              <span className="line-clamp-2 text-[10px] leading-tight text-neutral-300">
                {item.description ?? 'Quick action'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Icon tile only — spotlight is a sibling of the wrapping <button>/<a>. */}
      <motion.div
        ref={ref}
        style={{ width, height }}
        className={cn(
          'relative flex aspect-square items-center justify-center rounded-full bg-gray-200 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-[#d9c6b5] hover:shadow-[0_6px_18px_rgba(61,40,23,0.18)]',
          forceLight ? '' : 'dark:bg-neutral-800 dark:hover:bg-neutral-700',
          hovered
            ? cn('ring-1 ring-[#c9a77b]/55', forceLight ? '' : 'dark:ring-neutral-500/70')
            : 'ring-1 ring-transparent',
          isSpotlight && 'ring-2 ring-[#2f9e44]/60',
          itemClassName,
        )}
        animate={isSpotlight ? { y: [0, -8, 0] } : { y: 0 }}
        transition={
          isSpotlight
            ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.2 }
        }
      >
        <motion.div
          style={{ width: widthIcon, height: heightIcon }}
          className="relative z-10 flex items-center justify-center [&_svg]:shrink-0"
          animate={hovered || isSpotlight ? { scale: 1.06 } : { scale: 1 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {item.icon}
        </motion.div>
      </motion.div>
    </div>
  );

  const interactive =
    'to' in item ? (
      <Link to={item.to} className="inline-flex cursor-pointer text-inherit no-underline">
        {surface}
      </Link>
    ) : 'mobileSignedInAccount' in item && item.mobileSignedInAccount ? (
      <button
        type="button"
        onClick={item.onOpenPortal}
        className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-inherit"
      >
        {surface}
      </button>
    ) : 'languagePicker' in item && item.languagePicker ? (
      <DesktopLanguageDockButton item={item} surface={surface} />
    ) : (
      <button
        type="button"
        onClick={'onClick' in item ? item.onClick : undefined}
        className="inline-flex cursor-pointer border-0 bg-transparent p-0 text-inherit"
      >
        {surface}
      </button>
    );

  return (
    <div className="relative inline-flex">
      <AnimatePresence>
        {isSpotlight && spotlightContent ? (
          <DockSpotlightBubble key="dock-spotlight" anchorRef={ref}>
            {spotlightContent}
          </DockSpotlightBubble>
        ) : null}
      </AnimatePresence>
      {interactive}
    </div>
  );
}
